import { enumValue } from "@faire/typescript";
import { FairePillar } from "@faire/web-api--source/faire/logging/FairePillar";
import { FairePod } from "@faire/web-api--source/faire/logging/FairePod";
import { FairePodExtensions } from "@faire/web-api--source/faire/logging/FairePodExtensions";
import { ProductArea } from "@faire/web-api--source/faire/logging/ProductArea";
import { ProductAreaExtensions } from "@faire/web-api--source/faire/logging/ProductAreaExtensions";
import { Match, RouteMatcher } from "@faire/web/common/routes/RouteMatcher";
import { LocationDescriptor } from "history";

interface ProductAreaMatch<T extends string = string>
  extends Omit<Match<T, keyof typeof ProductArea>, "value"> {
  productArea: keyof typeof ProductArea;
  pod: keyof typeof FairePod;
  pillar: keyof typeof FairePillar;
}

/**
 * A wrapper around RouteMatcher that adds a method to match a route to the associated product area, pillar, and pod.
 */
export class ProductAreaRouteMatcher<
  RouteList extends string,
> extends RouteMatcher<RouteList, keyof typeof ProductArea> {
  constructor(routes: Record<RouteList, keyof typeof ProductArea>) {
    super(Object.entries(routes) as [RouteList, keyof typeof ProductArea][]);
  }

  matchProductArea = (
    pathOrLocation: string | LocationDescriptor
  ): ProductAreaMatch<RouteList> | undefined => {
    const routeMatch = this.match(pathOrLocation);
    if (!routeMatch) {
      return undefined;
    }
    const { route, match, value: productArea } = routeMatch;
    const productAreaEnum =
      enumValue(ProductArea, productArea) ?? ProductArea.PRODUCT_AREA_UNKNOWN;

    const pod =
      ProductAreaExtensions.getPod(productAreaEnum) ?? "FAIRE_POD_UNASSIGNED";
    const podEnum = enumValue(FairePod, pod) ?? FairePod.FAIRE_POD_UNASSIGNED;

    const pillar =
      FairePodExtensions.getPillar(podEnum) ?? "FAIRE_PILLAR_UNASSIGNED";

    return {
      route,
      match,
      productArea,
      pillar,
      pod,
    };
  };
}
